/**
 * O ROMANEIO EM PAPEL — o documento, não o formulário.
 *
 * A primeira versão mandava imprimir a própria tela, e o resultado foi o que
 * tinha de ser: rótulo sem valor (o comprador é um `<input>`, e input escondido
 * leva o valor junto), pergunta interna vazando para o papel («quantos peixes
 * ficaram na represa?» é conversa de dentro de casa), e nenhuma identificação
 * de quem emitiu.
 *
 * A lição vale além desta tela: FORMULÁRIO NÃO É DOCUMENTO. Um serve para
 * digitar, o outro para ser lido por quem não estava lá. Eles têm campos
 * diferentes, ordem diferente e destinatário diferente, e tentar economizar
 * fazendo um virar o outro custa as três coisas acima.
 *
 * ── O QUE NÃO ENTRA, e é de propósito ─────────────────────────────────────
 *
 *   O REMANESCENTE. Quantos peixes ficaram na represa é informação da
 *   fazenda sobre a fazenda. Não diz respeito a quem compra, e num papel que
 *   vai para a mão do comprador seria entregar o inventário junto com a nota.
 *
 *   O CONFRONTO com a estimativa. É conversa interna sobre a qualidade do
 *   próprio palpite do app. No papel do comprador não tem função nenhuma, e
 *   tem potencial de virar pergunta que ninguém precisa responder.
 *
 *   A REGRA DE ARREDONDAMENTO. Ela existe e está nos testes, mas explicá-la no
 *   rodapé é discussão de método — coisa de dentro de casa. Decisão do gestor
 *   em 20/09/2026: nota de venda não conversa sobre como foi calculada; ela
 *   fecha, e fechar é a única prova de que precisa dar.
 *
 * ── A LINGUAGEM VISUAL É A DOS CADERNOS ───────────────────────────────────
 *
 * Mesmo desenho dos Cadernos da Cunhataí: régua grossa sob o cabeçalho,
 * antetítulo em versalete espaçado, números em fonte de largura fixa para as
 * colunas alinharem, teal para a fazenda e ocre para o dinheiro. É o mesmo
 * grupo, e o papel que sai dele tem de se reconhecer.
 *
 * As fontes são as do próprio aparelho — Georgia, Helvetica, a monoespaçada
 * do sistema. Os Cadernos usam fontes da web porque nascem no computador do
 * gestor, com sinal; este papel nasce na beira do tanque, e um romaneio que
 * depende de baixar fonte é um romaneio que sai errado quando mais importa.
 */

import { createPortal } from 'react-dom'
import { emReais, type Romaneio } from '../dominio/romaneio'
import { inteiro, numero } from '../dominio/unidades'
import { Dado, emitidoEm, porExtenso } from './Papel'

export interface CabecalhoDoRomaneio {
  represa: string
  ciclo: string
  dia: string
  comprador: string
  destino: string
  observacao: string
}

/**
 * Vai para `document.body` por portal, e não para dentro da tela.
 *
 * É o que permite a regra de impressão ser uma linha só e não errar: esconde
 * todos os filhos diretos do corpo, menos este. Ficando dentro da tela, cada
 * ancestral teria de ser reexibido à mão, e bastaria alguém mudar a estrutura
 * da página para o papel sair em branco sem ninguém perceber.
 */
export function RomaneioPapel({
  romaneio, cabecalho, semValores = false,
}: {
  romaneio: Romaneio
  cabecalho: CabecalhoDoRomaneio
  /** O papel do tratador: pesos e contagem, sem preço nem valor (06/10/2026). */
  semValores?: boolean
}) {
  return createPortal(
    <div className="papel romaneio-papel">
      <header className="rp-capa">
        <div>
          <div className="rp-antetitulo">Sistema de Gestão · Piscicultura</div>
          <h1>Fazenda Cunhataí</h1>
          <div className="rp-lugar">Ariquemes — Rondônia</div>
        </div>
        <div className="rp-selo">
          <div className="rp-selo-titulo">Romaneio de despesca</div>
          <div className="rp-selo-data">{porExtenso(cabecalho.dia)}</div>
        </div>
      </header>

      <section className="rp-ficha">
        <Dado rotulo="Represa" valor={cabecalho.represa} />
        <Dado rotulo="Ciclo" valor={cabecalho.ciclo} />
        <Dado rotulo="Data da despesca" valor={porExtenso(cabecalho.dia)} />
        <Dado rotulo="Comprador" valor={cabecalho.comprador} largo />
        <Dado rotulo="Destino" valor={cabecalho.destino} largo />
      </section>

      <table className="rp-tabela">
        <thead>
          <tr>
            <th>Espécie</th>
            <th>Peixes</th>
            <th>Peso kg</th>
            <th>Desconto kg</th>
            <th>Líquido kg</th>
            {!semValores && <th>R$/kg</th>}
            {!semValores && <th>Valor R$</th>}
          </tr>
        </thead>
        <tbody>
          {romaneio.linhas.map((l) => (
            <tr key={l.especie}>
              <td className="rp-especie">{l.especie}</td>
              <td>{inteiro(l.peixes)}</td>
              <td>{numero(l.pesadoKg, 2)}</td>
              <td>{numero(l.descontoKg, 2)}</td>
              <td>{numero(l.cobradoKg, 2)}</td>
              {!semValores && <td>{l.precoKg === null ? '—' : numero(l.precoKg, 2)}</td>}
              {!semValores && <td>{l.valorBrl === null ? '—' : numero(l.valorBrl, 2)}</td>}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th className="rp-especie">Total</th>
            <th>{inteiro(romaneio.peixes)}</th>
            <th>{numero(romaneio.pesadoKg, 2)}</th>
            <th>{numero(romaneio.descontoKg, 2)}</th>
            <th>{numero(romaneio.cobradoKg, 2)}</th>
            {!semValores && <th />}
            {!semValores && <th>{romaneio.valorBrl === null ? '—' : numero(romaneio.valorBrl, 2)}</th>}
          </tr>
        </tfoot>
      </table>

      <section className="rp-fecho">
        <div className="rp-notas">
          <p>
            Peso médio de <strong>{numero(romaneio.mediaG / 1000, 3)} kg</strong> por peixe.
          </p>
          <p>
            Desconto de umidade de <strong>{numero(romaneio.descontoPct, 1)}%</strong> aplicado
            sobre o peso de todas as espécies, equivalente a{' '}
            <strong>{numero(romaneio.descontoKg, 2)} kg</strong>.
          </p>
          {cabecalho.observacao !== '' && <p>{cabecalho.observacao}</p>}
        </div>

        {!semValores && romaneio.valorBrl !== null && (
          <div className="rp-valor">
            <div className="rp-valor-rotulo">Valor total</div>
            <div className="rp-valor-numero">{emReais(romaneio.valorBrl)}</div>
          </div>
        )}
      </section>

      <section className="rp-assinaturas">
        <div>
          <div className="rp-risco" />
          <div className="rp-assinatura-rotulo">Fazenda Cunhataí</div>
        </div>
        <div>
          <div className="rp-risco" />
          <div className="rp-assinatura-rotulo">Recebedor</div>
        </div>
      </section>

      <footer className="rp-rodape">
        {emitidoEm()} pelo sistema de gestão da Fazenda Cunhataí. Pesos conferidos na
        balança da despesca; o desconto de umidade é abatimento comercial acordado entre as
        partes.
      </footer>
    </div>,
    document.body,
  )
}

